import { LaunchDarklyClientSide } from '@repairer-frontend/client-feature-flags';
import { initNewRelic } from '@ptml/observability';
import {
    isFeatureFlagProfile,
    mapToClientLdContext,
    type FeatureFlagProfile,
} from '@repairer-frontend/client-feature-flags';
import type { AppRouter as RootAppRouter } from '@repairer-frontend/root-trpc';
import { trpcFetchPolyfill } from '@repairer-frontend/trpc-client';
import {
    getApiBaseUrl,
    ROOT_CONFIG_LOCAL_STORAGE_KEY,
    buildPtusFrontendUrl,
} from '@ptml/root-config';
import { createTRPCClient, httpLink } from '@trpc/client';
import { USER_ROLES } from '@repairer-frontend/user-roles/utility';
import { generateConfig } from '@repairer-frontend/root-config';
import { login } from './auth/auth';
import { navigateToUrl, registerApplication, start } from 'single-spa';
import {
    constructApplications,
    constructLayoutEngine,
    constructRoutes,
} from 'single-spa-layout';
import { header, sidebar, settings, job } from './loaders';
import { ShouldDisableAnimations } from '@repairer-frontend/shared/client/client-app-test-support';
import { LayoutProvider } from './LayoutProvider';
import desktopLayout from './desktop-layout.html?raw';
import mobileLayout from './mobile-layout.html?raw';
import { isProduction } from '@repairer-frontend/client-app-environment';
import { getHomepageUrl } from '@repairer-frontend/user-roles/utility';
import { isMobile } from '@ptml/mobile-helpers';
import { LAYOUT_EVENTS, setAppReady } from '@repairer-frontend/shared-ui';
import { startViewportTelemetry } from './telemetry/viewportTelemetry';

const isCypressRunning = window['Cypress'];
const isPlaywrightRunning = window['__playwright__'];

const checkImportMapVisibility = () => {
    const importMapUI = document.querySelector('import-map-overrides-full');
    if (importMapUI && importMapUI instanceof HTMLElement) {
        importMapUI.style.display = isMobile() ? 'none' : '';
    }
};

const observeImportMapChanges = () => {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                for (const node of mutation.addedNodes) {
                    if (
                        node instanceof HTMLElement &&
                        node.tagName.toLowerCase() ===
                            'import-map-overrides-full'
                    ) {
                        checkImportMapVisibility();
                        break;
                    }
                }
            }
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });

    return observer;
};

if (!isProduction()) {
    if (!isCypressRunning && !isPlaywrightRunning && !isMobile()) {
        const importMapOverrideDevTools = document.createElement(
            'import-map-overrides-full',
        );
        importMapOverrideDevTools['dev-libs'] = 'dev-libs';
        document.body.append(importMapOverrideDevTools);

        // Use MutationObserver to watch for DOM changes instead of setTimeout
        observeImportMapChanges();

        // Initial check in case the element is already present
        checkImportMapVisibility();
    }

    // add field to the window object required for Cypress code coverage
    window['__coverage__'] = window['__coverage__'] || {};
}

// Vite production builds are deployed to every environment, not only the production hostname.
const browserAgent =
    import.meta.env.PROD && !isCypressRunning && !isPlaywrightRunning
        ? initNewRelic({
              accountId: '2839186',
              trustKey: '739791',
              agentId: '601448608',
              licenseKey: 'NRBR-4ed05250c3c6b0af49e',
              applicationId: '601448608',
              allowedOrigins: [new URL(getApiBaseUrl()).origin],
          })
        : null;

/**
 * Dev only function to inject the vite refresh snippet into the page.
 * @param baseUrl
 */
const injectViteRefreshSnippet = (baseUrl: string) => {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'module';
    scriptElement.textContent = `
            import { injectIntoGlobalHook } from "${baseUrl}/@react-refresh";

            injectIntoGlobalHook(window);
            window.$RefreshReg$ = () => {};
            window.$RefreshSig$ = () => (type) => type;
        `;
    document.head.append(scriptElement);
};

const disableAnimationsIfRunningTests = () => {
    if (ShouldDisableAnimations()) {
        document.body.classList.add('no-animations');
    }
};

const createRootTrpcClient = () => {
    return createTRPCClient<RootAppRouter>({
        links: [
            httpLink({
                url: `${getApiBaseUrl()}/fss/repairer/bff/root`,
                fetch: trpcFetchPolyfill,
                headers() {
                    return {
                        // Required header for the FSS
                        'x-csrf': '1',
                    };
                },
            }),
        ],
    });
};

const getFeatureFlagProfile = async (): Promise<FeatureFlagProfile | null> => {
    try {
        const profile =
            await createRootTrpcClient().getFeatureFlagProfile.query();

        return isFeatureFlagProfile(profile) ? profile : null;
    } catch (error) {
        console.error('Failed to fetch FeatureFlagProfile', error);
        return null;
    }
};

const initialize = () => {
    const data = {
        loaders: {
            header,
            sidebar,
            job,
            settings,
        },
        props: {
            config: { baseUrl: '/' },
            handleError: (error: Error) => {
                console.error('Error in root', error);
            },
        },
    };

    new LayoutProvider();

    const importMap = JSON.parse(
        document.querySelector('script[type=importmap]')?.textContent ?? '{}',
    );

    // de-register the old cache service worker
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
            void registration.unregister();
        }
    });

    const enableImportMapOverrides =
        !(
            window.location.hostname === 'repairer.partstrader.us.com' ||
            window.location.hostname === 'myorderly.partstrader.us.com'
        ) &&
        !isCypressRunning &&
        !isPlaywrightRunning &&
        !isMobile();

    // Manually add the import map script for non-production environments
    // Don't add it for mobile devices, when cypress is running, or production environments
    if (enableImportMapOverrides) {
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.src =
            'https://cdn.jsdelivr.net/npm/import-map-overrides@4.2.0/dist/import-map-overrides.min.js';
        script.fetchPriority = 'low';
        script.async = true;
        script.defer = true;

        document.head.append(script);
    }

    const layoutToLoad = isMobile() ? mobileLayout : desktopLayout;

    const routes = constructRoutes(layoutToLoad, data);
    const applications = constructApplications({
        routes,
        loadApp({ name }: { name: string }) {
            const pathToApp =
                localStorage.getItem(`import-map-override:${name}`) ??
                importMap.imports[name];

            // enable HMR for any apps that are running locally
            // Skip during Cypress/Playwright — react-refresh's DevTools hook patching is
            // incompatible with the React 19 production CDN build and causes
            // `E.getOwner is not a function` crashes during test runs.
            if (
                pathToApp.includes('http://localhost') &&
                process.env.NODE_ENV !== 'localdev' &&
                !isCypressRunning &&
                !isPlaywrightRunning
            ) {
                injectViteRefreshSnippet(new URL(pathToApp).origin);
            }

            return import(/* @vite-ignore */ pathToApp);
        },
    });
    const layoutEngine = constructLayoutEngine({
        routes,
        applications,
    });

    for (const app of applications) {
        registerApplication(app);
    }

    layoutEngine.activate();
    start();
    disableAnimationsIfRunningTests();
};

initialize();

/**
 * Initializes the root config settings with the provided user data.
 * @param userData The user data used for initialization.
 */
// TODO TECH-4194 In the future using top-level await might be cause a backwards compatibility break in Node
login().then(async (userData) => {
    if (!userData) {
        window.location.href = `${getApiBaseUrl()}/fss/repairer/auth/login?returnUrl=${window.location.href}`;
        return;
    }
    const repairerRoles = new Set<string>(USER_ROLES);
    if (userData?.roles.some((role) => repairerRoles.has(role))) {
        const config = await generateConfig(
            userData,
            import.meta?.env?.VITE_LaunchDarkly_repairer_vnext_Client_Sdk_Key,
        );
        const { user = null } = config;

        if (user?.principal_accountid && browserAgent) {
            browserAgent.setUserId(user.principal_accountid);
        }

        window.localStorage.setItem(
            ROOT_CONFIG_LOCAL_STORAGE_KEY,
            JSON.stringify(config),
        );
        window.dispatchEvent(
            new StorageEvent('local-storage', {
                key: ROOT_CONFIG_LOCAL_STORAGE_KEY,
            }),
        );

        // Fetch the feature flag profile for the user and map it to a LaunchDarkly context
        // for an enriched, full context. If the profile can't be fetched for any reason,
        // fall back to a context with just the company ID to avoid blocking the app from loading.
        const featureFlagProfile = await getFeatureFlagProfile();
        const ldContext = featureFlagProfile
            ? mapToClientLdContext(featureFlagProfile)
            : {
                  kind: 'user',
                  key: user ? `c-${user.company_displayid}` : '',
              };

        await LaunchDarklyClientSide({
            envKey: config.launchDarklyClientId,
            context: ldContext,
        });

        const resolvedFlags = window.localStorage.getItem('featureFlags');
        const parsedFlags = resolvedFlags ? JSON.parse(resolvedFlags) : {};
        const hasAccessToOrderly = parsedFlags['Access-Orderly-App'] === true;
        if (!hasAccessToOrderly) {
            window.location.href = buildPtusFrontendUrl('');
            return;
        }

        if (window.location.pathname === '/') {
            const homeUrl = getHomepageUrl(userData, repairerRoles);
            navigateToUrl(homeUrl);
        }

        // We block user interaction on the page using the inert attribute on
        // the body until the app is fully loaded (especially feature flags) to
        // avoid weird behaviour. We do this so we can load the apps, user data
        // and feature flags all asynchronously with no blocking
        document.body.attributes.removeNamedItem('inert');

        // Set the global flag before dispatching so late-mounting
        // micro-frontends can detect that the app is already ready
        setAppReady();

        // Dispatch a custom event to signal that the app is ready and user data and feature flags are loaded
        document.dispatchEvent(new CustomEvent(LAYOUT_EVENTS.APP_READY));

        // Started post-login so redirected/unauthorised visits don't add noise
        if (browserAgent) {
            startViewportTelemetry(browserAgent);
        }
    } else {
        window.location.href = buildPtusFrontendUrl('');
    }
});
